import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Filter, Check } from 'lucide-react';
import clsx from 'clsx';

export interface ColumnFilterValue {
  value: string;
  label: string;
  count?: number;
  separator?: boolean;
}

interface ColumnFilterProps {
  values: ColumnFilterValue[];
  selectedValues: string[];
  onChange: (selected: string[]) => void;
  className?: string;
  alwaysVisible?: boolean;
}

export function ColumnFilter({
  values,
  selectedValues,
  onChange,
  className,
  alwaysVisible = false,
}: ColumnFilterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [isPositioned, setIsPositioned] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const hasActiveFilter = selectedValues.length > 0;

  const updatePosition = useCallback(() => {
    if (!buttonRef.current || !dropdownRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const dropdownWidth = 300;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    let left = rect.left;
    if (left + dropdownWidth > viewportWidth - 8) {
      left = Math.max(8, rect.right - dropdownWidth);
    }
    const top = rect.bottom + 4;
    const maxH = viewportHeight - top - 8;
    setDropdownPosition({ top, left });
    dropdownRef.current.style.maxHeight = `${Math.max(maxH, 120)}px`;
    setIsPositioned(true);
  }, []);

  // Position dropdown when opened
  useEffect(() => {
    if (isOpen) {
      setIsPositioned(false);
      requestAnimationFrame(updatePosition);
    }
  }, [isOpen, updatePosition]);

  // Close on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      if (
        buttonRef.current && !buttonRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false);
        setSearchTerm('');
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Focus search input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const filteredValues = useMemo(() => {
    if (!searchTerm) return values;
    const lower = searchTerm.toLowerCase();
    return values.filter(
      (v) =>
        !v.separator && (
          v.label.toLowerCase().includes(lower) ||
          v.value.toLowerCase().includes(lower)
        )
    );
  }, [values, searchTerm]);

  const handleToggle = (value: string) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter((v) => v !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };

  const handleSelectAll = () => {
    onChange(values.filter((v) => !v.separator).map((v) => v.value));
  };

  const handleClearAll = () => {
    onChange([]);
  };

  return (
    <div className={clsx('relative inline-flex', className)}>
      <button
        ref={buttonRef}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className={clsx(
          'p-0.5 rounded-sm transition-colors',
          hasActiveFilter
            ? 'text-primary-400 hover:text-primary-300'
            : clsx(
                'text-dark-text-muted hover:text-dark-text-secondary',
                !alwaysVisible && 'opacity-0 group-hover:opacity-100'
              )
        )}
        title="Filter column"
      >
        <Filter className="w-3.5 h-3.5" />
      </button>

      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          className={clsx(
            'fixed z-9999 bg-dark-elevated border border-dark-border rounded-lg shadow-xl min-w-[220px] max-w-[300px] flex flex-col overflow-hidden',
            isPositioned ? 'animate-scaleIn' : 'opacity-0 pointer-events-none'
          )}
          style={isPositioned
            ? { top: dropdownPosition.top, left: dropdownPosition.left }
            : { top: -9999, left: -9999 }
          }
          onClick={(e) => e.stopPropagation()}
        >
          {/* Search input */}
          <div className="p-2 border-b border-dark-border">
            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search..."
              className="w-full px-2 py-1.5 text-sm bg-dark-surface border border-dark-border rounded-sm text-dark-text-primary placeholder:text-dark-text-muted focus:outline-hidden focus:border-primary-500"
            />
          </div>

          {/* Quick actions */}
          <div className="flex items-center gap-2 px-2 py-1.5 border-b border-dark-border text-xs">
            <button
              onClick={handleSelectAll}
              className="text-primary-400 hover:text-primary-300 transition-colors"
            >
              Select all
            </button>
            <span className="text-dark-text-muted">|</span>
            <button
              onClick={handleClearAll}
              className="text-dark-text-secondary hover:text-dark-text-primary transition-colors"
            >
              Clear
            </button>
            {hasActiveFilter && (
              <>
                <span className="text-dark-text-muted">|</span>
                <span className="text-dark-text-muted">
                  {selectedValues.length} selected
                </span>
              </>
            )}
          </div>

          {/* Values list */}
          <div className="flex-1 overflow-y-auto py-1">
            {filteredValues.length === 0 ? (
              <div className="px-3 py-2 text-sm text-dark-text-muted">
                No matching values
              </div>
            ) : (
              filteredValues.map((item, idx) => {
                if (item.separator) {
                  return <div key={`sep-${idx}`} className="border-t border-dark-border my-1" />;
                }
                const isSelected = selectedValues.includes(item.value);
                return (
                  <button
                    key={item.value}
                    onClick={() => handleToggle(item.value)}
                    className={clsx(
                      'w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left transition-colors',
                      isSelected
                        ? 'bg-primary-600/20 text-primary-300'
                        : 'text-dark-text-secondary hover:bg-dark-surface'
                    )}
                  >
                    <div
                      className={clsx(
                        'w-4 h-4 rounded-sm border flex items-center justify-center flex-shrink-0',
                        isSelected
                          ? 'bg-primary-500 border-primary-500'
                          : 'border-dark-border'
                      )}
                    >
                      {isSelected && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <span className="flex-1 truncate">{item.label}</span>
                    {item.count !== undefined && (
                      <span className="text-xs text-dark-text-muted">
                        ({item.count})
                      </span>
                    )}
                  </button>
                );
              })
            )}
          </div>

          {/* Apply/Close footer */}
          <div className="flex items-center justify-end gap-2 px-2 py-2 border-t border-dark-border">
            <button
              onClick={() => {
                setIsOpen(false);
                setSearchTerm('');
              }}
              className="px-3 py-1 text-xs text-dark-text-secondary hover:text-dark-text-primary transition-colors"
            >
              Close
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
